<script lang="ts">
  import { travelTime, candidates, activeCandidateId, friends, trips } from '../lib/state';
  import { travelApprox, reachableFriends, reachableTrips } from '../lib/maps/overlays';
  import { MODE_COLORS } from '../lib/scoring';
  import type { TravelMode } from '../lib/types';

  let dismissed = false;

  // Re-show the panel whenever the selected candidate changes.
  $: if ($activeCandidateId) dismissed = false;

  // The radius is always on now that the "Show radius" toggle is gone; repair
  // any saved state that had it disabled.
  $: if (!$travelTime.enabled) travelTime.update((t) => ({ ...t, enabled: true }));

  // Keep a valid candidate selected so the radius always has a subject.
  $: if ($candidates.length && !$candidates.some((c) => c.id === $activeCandidateId)) {
    activeCandidateId.set($candidates[0].id);
  }

  const modes: { mode: TravelMode; icon: string; label: string }[] = [
    { mode: 'WALKING', icon: '🚶', label: 'Walk' },
    { mode: 'BICYCLING', icon: '🚲', label: 'Bike' },
    { mode: 'TRANSIT', icon: '🚌', label: 'Transit' },
    { mode: 'DRIVING', icon: '🚗', label: 'Drive' },
  ];

  const modeVerb: Record<TravelMode, string> = {
    WALKING: 'walk',
    BICYCLING: 'bike',
    TRANSIT: 'transit',
    DRIVING: 'drive',
  };

  function formatMinutes(n: number): string {
    if (n < 60) return `${n} min`;
    const h = Math.floor(n / 60);
    const rem = n % 60;
    return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
  }

  $: visible = $candidates.length > 0 && !dismissed;
  $: reachCount = $reachableFriends.length;
  $: tripCount = $reachableTrips.length;
  $: activeMode = modes.find((m) => m.mode === $travelTime.mode);
  $: heroText = `${formatMinutes($travelTime.minutes)} ${modeVerb[$travelTime.mode]}`;

  // Mobile shows the panel as a collapsed pill in the map header that expands
  // into a card; on desktop the panel is always the open card (CSS decides).
  let expanded = false;
</script>

<div class="tt-pill-wrap" class:visible>
  <button
    class="tt-pill"
    id="tt-pill"
    aria-expanded={expanded}
    aria-controls="tt-card"
    on:click={() => (expanded = !expanded)}
  >
    <span class="tt-pill-icon" aria-hidden="true">{activeMode?.icon}</span>
    <span class="tt-pill-text" style="color:{MODE_COLORS[$travelTime.mode]}">{heroText}</span>
    <span class="tt-pill-chev" aria-hidden="true">{expanded ? '▲' : '▼'}</span>
  </button>
</div>

<div
  class="travel-time-panel"
  id="tt-card"
  class:visible
  class:expanded
  class:is-approx={$travelApprox}
  aria-label="Travel time radius"
>
  <button class="tt-close" on:click={() => (dismissed = true)} aria-label="Close">×</button>
  <div class="tt-meta">
    <label class="tt-from">
      <span class="tt-from-label">Radius from</span>
      <select class="tt-candidate-select" bind:value={$activeCandidateId} aria-label="Candidate for travel radius">
        {#each $candidates as c (c.id)}
          <option value={c.id}>{c.name}</option>
        {/each}
      </select>
    </label>
    <span class="tt-approx" title="Transit isochrones aren't available. Showing a straight-line estimate">≈ approximate</span>
  </div>
  <div class="tt-hero" style="color:{MODE_COLORS[$travelTime.mode]}">
    <span class="tt-hero-icon" aria-hidden="true">{activeMode?.icon}</span>
    <span class="tt-hero-text">{heroText}</span>
  </div>
  <div class="tt-row tt-slider-row">
    <input
      type="range"
      class="tt-slider"
      min="5"
      max="60"
      step="5"
      bind:value={$travelTime.minutes}
      aria-label="Travel time minutes"
    />
  </div>
  <div class="tt-modes" role="radiogroup" aria-label="Travel mode">
    {#each modes as m}
      <button
        class="tt-mode"
        class:active={$travelTime.mode === m.mode}
        role="radio"
        aria-checked={$travelTime.mode === m.mode}
        on:click={() => travelTime.update((t) => ({ ...t, mode: m.mode }))}
      >
        <span class="tt-mode-icon" aria-hidden="true">{m.icon}</span>
        <span class="tt-mode-label">{m.label}</span>
      </button>
    {/each}
  </div>
  <!-- Compact reach summary — the card is short on mobile, so people and places
       collapse onto one line with a single merged chip run. Desktop keeps the
       two labelled sections below. -->
  {#if $travelTime.enabled && ($friends.length || $trips.length)}
    <div class="tt-summary">
      <div class="tt-summary-line">
        {#if $friends.length}<strong>{reachCount}</strong> of your {$friends.length} people{/if}
        {#if $friends.length && $trips.length}<span class="tt-summary-sep">·</span>{/if}
        {#if $trips.length}<strong>{tripCount}</strong> of your {$trips.length} places{/if}
      </div>
      {#if reachCount || tripCount}
        <ul class="tt-summary-chips">
          {#each $reachableFriends as f (f.id)}
            <li class="tt-person"><span class="tt-person-dot" aria-hidden="true">🙂</span>{f.name}</li>
          {/each}
          {#each $reachableTrips as t (t.id)}
            <li class="tt-person tt-spot"><span class="tt-person-dot" aria-hidden="true">📍</span>{t.name}</li>
          {/each}
        </ul>
      {:else}
        <p class="tt-people-empty">Nothing in reach yet. Try a longer time or another mode.</p>
      {/if}
    </div>
  {/if}

  {#if $travelTime.enabled && $friends.length}
    <div class="tt-people">
      <div class="tt-people-head">
        <strong>{reachCount}</strong>
        of your {$friends.length} people within {formatMinutes($travelTime.minutes)} {modeVerb[$travelTime.mode]}
      </div>
      {#if reachCount}
        <ul class="tt-people-list">
          {#each $reachableFriends as f (f.id)}
            <li class="tt-person"><span class="tt-person-dot" aria-hidden="true">🙂</span>{f.name}</li>
          {/each}
        </ul>
      {:else}
        <p class="tt-people-empty">No one in reach yet. Try a longer time or another mode.</p>
      {/if}
    </div>
  {/if}

  {#if $travelTime.enabled && $trips.length}
    <div class="tt-people tt-spots">
      <div class="tt-people-head">
        <strong>{tripCount}</strong>
        of your {$trips.length} places within {formatMinutes($travelTime.minutes)} {modeVerb[$travelTime.mode]}
      </div>
      {#if tripCount}
        <ul class="tt-people-list">
          {#each $reachableTrips as t (t.id)}
            <li class="tt-person tt-spot"><span class="tt-person-dot" aria-hidden="true">📍</span>{t.name}</li>
          {/each}
        </ul>
      {:else}
        <p class="tt-people-empty">No spots in reach yet. Try a longer time or another mode.</p>
      {/if}
    </div>
  {/if}
</div>

<style>
  .tt-from { display: flex; align-items: baseline; gap: 6px; min-width: 0; }
  .tt-from-label {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--ink-soft);
    flex: none;
  }
  .tt-candidate-select {
    font-family: var(--sans);
    font-size: 12.5px;
    font-weight: 600;
    color: var(--ink);
    background: transparent;
    border: none;
    border-bottom: 1px dashed var(--line);
    padding: 1px 2px;
    max-width: 150px;
    cursor: pointer;
  }
  .tt-candidate-select:focus { outline: none; border-bottom-color: var(--accent); }

  .tt-hero {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 2px 0 10px 0;
    line-height: 1;
  }
  .tt-hero-icon { font-size: 24px; line-height: 1; }
  .tt-hero-text {
    font-family: var(--serif);
    font-style: italic;
    font-size: 28px;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.01em;
  }

  /* Compact reach summary — revealed only inside the mobile card
     (see .travel-time-panel.expanded in app.css). */
  .tt-summary {
    display: none;
    padding-top: 12px;
    border-top: 1px solid var(--line);
  }
  .tt-summary-line {
    font-size: 12px;
    color: var(--ink-soft);
    line-height: 1.35;
  }
  .tt-summary-line strong {
    font-family: var(--serif);
    font-style: italic;
    font-size: 18px;
    color: var(--accent);
    margin-right: 2px;
  }
  .tt-summary-sep { margin: 0 3px; }
  .tt-summary-chips {
    list-style: none;
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    margin: 9px 0 0;
    padding: 0;
  }

  .tt-people {
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px solid var(--line);
  }
  .tt-people-head {
    font-size: 12px;
    color: var(--ink-soft);
    line-height: 1.35;
  }
  .tt-people-head strong {
    font-family: var(--serif);
    font-style: italic;
    font-size: 17px;
    color: var(--accent);
    margin-right: 2px;
  }
  .tt-people-list {
    list-style: none;
    margin: 8px 0 0 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    max-height: 168px;
    overflow-y: auto;
  }
  .tt-person {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: rgba(90,138,58,0.12);
    border: 1px solid rgba(90,138,58,0.35);
    color: var(--ink);
    border-radius: 999px;
    padding: 2px 9px 2px 6px;
    font-size: 11.5px;
    font-weight: 500;
    white-space: nowrap;
  }
  .tt-spot {
    background: rgba(184,134,31,0.12);
    border-color: rgba(184,134,31,0.4);
  }
  .tt-person-dot { font-size: 11px; line-height: 1; }
  .tt-people-empty {
    margin: 8px 0 0 0;
    font-size: 11.5px;
    color: var(--ink-soft);
    line-height: 1.4;
  }
</style>
